import React from "react";
import { Link } from "react-router-dom";
import { Inbox } from "lucide-react";
import { URGENCY, fmtDate } from "@/lib/brand";
import { useAuth } from "@/context/AuthContext";

export const StatusPill = ({ urgency, children, testid }) => {
  const u = URGENCY[urgency] || URGENCY.upcoming;
  return <span className={`pm-chip ${u.cls}`} data-testid={testid}>{children || u.label}</span>;
};

export const PageHeader = ({ title, subtitle, actions }) => (
  <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-8 pm-fade-up">
    <div>
      <h1 className="text-3xl sm:text-4xl font-extrabold" data-testid="page-title">{title}</h1>
      {subtitle && <p className="text-[var(--pm-muted)] mt-1.5 text-sm sm:text-base">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2.5 no-print">{actions}</div>}
  </div>
);

export const EmptyState = ({ icon: Icon = Inbox, title, text, action, testid = "empty-state" }) => (
  <div className="flex flex-col items-center text-center py-14 px-6" data-testid={testid}>
    <div className="w-14 h-14 rounded-2xl bg-[#F2EFFC] grid place-items-center mb-4"><Icon className="w-6 h-6 text-[var(--pm-primary)]" /></div>
    <h3 className="font-bold text-lg">{title}</h3>
    {text && <p className="text-sm text-[var(--pm-muted)] mt-1 max-w-sm">{text}</p>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

export const SkeletonRows = ({ rows = 4 }) => (
  <div className="space-y-3" data-testid="loading-skeleton">
    {Array.from({ length: rows }).map((_, i) => <div key={i} className="pm-skeleton h-14" />)}
  </div>
);

export const Card = ({ title, action, children, className = "", testid }) => (
  <section className={`pm-card p-5 sm:p-6 min-w-0 ${className}`} data-testid={testid}>
    {(title || action) && (
      <div className="flex items-center justify-between mb-4">
        {title && <h2 className="font-bold text-lg">{title}</h2>}
        {action}
      </div>
    )}
    {children}
  </section>
);

export const useDateFmt = () => {
  const { user } = useAuth();
  const pattern = user?.prefs?.date_format || "MMM d, yyyy";
  return (iso) => fmtDate(iso, pattern);
};

export function ReminderRow({ r, onClick }) {
  const f = useDateFmt();
  return (
    <Link to={`/reminders/${r.id}`} onClick={onClick}
      className="flex items-center gap-4 py-3.5 px-2 -mx-2 rounded-xl hover:bg-[#F8F6FF] transition-colors border-b border-[var(--pm-border)] last:border-0"
      data-testid={`reminder-row-${r.id}`}>
      <span className="w-10 h-10 shrink-0 rounded-xl grid place-items-center text-white text-sm font-bold"
        style={{ background: (URGENCY[r.urgency] || URGENCY.upcoming).dot }}>{r.title.slice(0, 1).toUpperCase()}</span>
      <span className="min-w-0 flex-1">
        <span className="block font-semibold truncate">{r.title}</span>
        <span className="block text-xs text-[var(--pm-muted)] truncate">{[r.subcategory_name || r.category_name, r.owner_name].filter(Boolean).join(" · ")}</span>
      </span>
      <span className="hidden sm:block text-xs text-[var(--pm-muted)]">{f(r.next_date)}</span>
      <StatusPill urgency={r.urgency} testid={`reminder-countdown-${r.id}`}>{r.status === "active" ? r.countdown : URGENCY[r.status]?.label}</StatusPill>
    </Link>
  );
}

export const Field = ({ label, required, error, children, hint, htmlFor }) => (
  <div>
    {label && <label className="pm-label" htmlFor={htmlFor}>{label}{required && <span className="text-[var(--pm-danger)]"> *</span>}</label>}
    {children}
    {hint && !error && <p className="text-xs text-[var(--pm-muted)] mt-1">{hint}</p>}
    {error && <p className="text-xs text-[var(--pm-danger)] mt-1" role="alert">{error}</p>}
  </div>
);
